import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import './Wallet.css';

const PaymentRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id') || searchParams.get('sessionId');
      const orderRef = searchParams.get('order_ref') || 
                      searchParams.get('orderRef') || 
                      searchParams.get('referenceId') || 
                      searchParams.get('reference_id');
      const paymentStatus = searchParams.get('status');

      // Debug: Log all URL parameters
      console.log('🔍 All URL parameters:', Object.fromEntries(searchParams.entries()));
      console.log('🔍 Full URL:', window.location.href);
      console.log('🔙 PAYMENT REDIRECT RECEIVED:', { sessionId, orderRef, paymentStatus });

      // Handle cancel status immediately
      if (paymentStatus === 'cancelled') {
        console.log('❌ Payment was cancelled by user');
        setStatus('error');
        setMessage('❌ Payment was cancelled. No charges were made.');
        setTimeout(() => navigate('/dashboard', { state: { activeTab: 'wallet', showOrders: true } }), 3000);
        return;
      }

      if (!orderRef && !sessionId) {
        console.error('❌ No order reference or session ID in redirect URL');
        console.error('Available params:', Array.from(searchParams.keys()));
        setStatus('error');
        setMessage('Invalid payment reference. Please check your wallet to see if payment was processed.');
        setTimeout(() => navigate('/dashboard', { state: { activeTab: 'wallet' } }), 4000);
        return;
      }

      try {
        // Try with order_ref first, fall back to session_id
        let apiUrl;
        if (orderRef) {
          console.log('🔍 Verifying payment with order reference:', orderRef);
          apiUrl = `/api/wallet/topup/verify/${orderRef}`;
        } else if (sessionId) {
          console.log('🔍 Verifying payment with session ID:', sessionId);
          apiUrl = `/api/wallet/topup/verify/session/${sessionId}`;
        }
        
        const response = await api.get(apiUrl);
        
        console.log('📊 Backend verification response:', response.data);
        
        if (response.data.status === 'COMPLETED') {
          console.log('✅ Payment verified successfully! Amount:', response.data.amount);
          setStatus('success');
          setMessage('✅ Payment successful! Your enrollment has been updated.');
          // Redirect to dashboard wallet tab after 2 seconds
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { 
                activeTab: 'wallet',
                showOrders: true,
                walletUpdated: true,
                message: 'Payment successful! Your enrollment has been updated.'
              }
            });
          }, 2000);
        } else if (response.data.status === 'PROCESSING') {
          console.log('⏳ Payment is being processed');
          setStatus('pending');
          setMessage('⏳ Payment is being processed. Please check back shortly.');
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { activeTab: 'wallet', showOrders: true }
            });
          }, 3000);
        } else if (response.data.status === 'PENDING') {
          console.log('⏳ Payment pending');
          setStatus('pending');
          setMessage('⏳ Payment is being processed. Please check your wallet in a few moments.');
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { activeTab: 'wallet', showOrders: true }
            });
          }, 3000);
        } else if (response.data.status === 'EXPIRED') {
          console.log('⚠️ Payment expired');
          setStatus('error');
          setMessage('⚠️ Payment session expired. Please try again.');
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { activeTab: 'wallet', showOrders: true }
            });
          }, 3000);
        } else if (response.data.status === 'FAILED') {
          console.log('❌ Payment failed');
          setStatus('error');
          setMessage('❌ Payment failed or was cancelled.');
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { activeTab: 'wallet', showOrders: true }
            });
          }, 3000);
        } else {
          console.log('⚠️ Unknown payment status:', response.data.status);
          setStatus('warning');
          setMessage(`Payment status: ${response.data.message || response.data.status}`);
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { activeTab: 'wallet', showOrders: true }
            });
          }, 3000);
        }
      } catch (error) {
        console.error('❌ Payment verification error:', error);
        console.error('Error details:', error.response?.data || error.message);
        setStatus('error');
        setMessage('Failed to verify payment. Please check your wallet.');
        setTimeout(() => navigate('/dashboard'), 3000);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  const getStatusColor = () => {
    switch (status) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'pending': return '#f59e0b';
      case 'warning': return '#f59e0b';
      default: return '#667eea';
    }
  };

  return (
    <div className="wallet-container" style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <div className="payment-verification" style={{
        background: `linear-gradient(135deg, ${getStatusColor()} 0%, ${getStatusColor()}dd 100%)`
      }}>
        {status === 'verifying' && (
          <div className="spinner"></div>
        )}
        {status === 'success' && (
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
        )}
        {status === 'error' && (
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>❌</div>
        )}
        {status === 'pending' && (
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>⏳</div>
        )}
        <h3>{status === 'verifying' ? '🔒 Processing Payment' : 'Payment Status'}</h3>
        <p>{message}</p>
        <p className="verification-note">
          {status === 'verifying' ? 'Please wait...' : 'Redirecting to dashboard...'}
        </p>
      </div>
    </div>
  );
};

export default PaymentRedirect;
